/**
 * Bundle & Save — below-ATC set upsell.
 * Spec: docs/superpowers/specs/2026-07-25-pdp-bundle-save-set-upsell-design.md
 * Degradation contract: any failure → the unit stays hidden. The loading
 * skeleton shows only while the suggestions request is genuinely in flight;
 * an error or an empty result still leaves nothing behind.
 */

import WishlistState from './wishlist-state.js';
import { wallLayoutSnapshot } from './bundle-save-wall-snapshot.js';
import { reportedVariantIds, piecesToPush, pieceAtcPush } from './bundle-save-atc.js';

const READY_TRIES = 80; // 8s, matching pdp-surgical-config's whenSkuPickerReady
// How long to wait for the Stape embed's own add_to_cart_stape push before
// concluding it will never come. Generous on purpose: nothing the shopper sees
// is waiting on it, and concluding too early double-reports a piece to Meta.
const EMBED_PUSH_WAIT_MS = 8000;
const EMBED_PUSH_POLL_MS = 100;
// Placeholder marker used to splice a control into a locale string's {{ name }}
// slot without hardcoding word order.
const SPEC_SLOT = String.fromCharCode(0);

// Set layout → the canonical wall layout handle (/a/advisor/api/layouts, the
// same handles the gallery-wall builder routes on). Two things need it:
//   * the advisor handoff, which otherwise picks whichever layout matches the
//     frame count first and reshuffles the wall the shopper just reviewed;
//   * the _gallery_wall_layout cart property, which the cart's bundle card
//     turns into /a/gallery-wall/build/<handle> — our own layout keys don't
//     resolve there, so the card linked to the gallery-wall landing page.
// Hanging gap between prints, scaled to the print itself rather than a flat
// number: a gap sized right for a 24in print reads as a canyon next to a 9in
// one (QA). One function on purpose, same as the constant it replaces: the
// room composite HANGS the wall at this gap and the card/spec "total wall
// size" lines COMPUTE from it — two numbers here and the picture contradicts
// the caption.
//
// Basis is the print's SHORTER side, so a portrait and landscape print of the
// "same" size hang with the same gap rather than the gap changing with
// orientation. 1/6 of that side lands on the previous flat value at 24in
// (24/6 = 4, the size QA had approved) while scaling down for smaller prints;
// clamped so a 40in+ print doesn't open up an implausibly wide gap.
//
// The floor started at 1.5in — QA's next round: the catalogue's three
// smallest sizes (9, 11, 12in short side) all landed under 2in and read as
// crowded together, not merely tight. 2.5in as the floor means those three
// tie (this size range has no room for 1/6-scaling to tell them apart
// anyway — the gap between clamp and the next unclamped size, 18in's 3in, is
// where scaling resumes).
function hangGapInches(widthInches, heightInches) {
  const shortSide = Math.min(widthInches, heightInches);
  return Math.min(5, Math.max(2.5, shortSide / 6));
}

const WALL_LAYOUTS = {
  'pair-2': 'diptych',
  'trio-3': 'triptych-medium',
  'quad-2x2': 'grid-2x2',
  'salon-5': 'salon-style',
  'salon-6': 'the-classic-six',
};

function whenSkuPickerReady(cb, onTimeout) {
  if (window.skuPickerInstance && window.skuPickerInstance.data) return cb(window.skuPickerInstance);
  let tries = 0;
  const t = setInterval(() => {
    tries++;
    if (window.skuPickerInstance && window.skuPickerInstance.data) {
      clearInterval(t);
      cb(window.skuPickerInstance);
    } else if (tries > READY_TRIES) {
      clearInterval(t);
      onTimeout?.();
    }
  }, 100);
}

export function pieceTotalCents(pricing, sel) {
  const s = pricing?.size?.[sel.size_option];
  if (typeof s !== 'number') return null; // unpriceable at this size
  const f = pricing?.framing?.[`${sel.size_option}|${sel.framing_option}`] ?? 0;
  const b = pricing?.border?.[`${sel.size_option}|${sel.border_option}`] ?? 0;
  return s + f + b;
}

/**
 * The saving for this set at this selection, in cents.
 *
 * The API returns a table keyed "<size>|<framing>" because the saving is set
 * per configuration — a pair of 9x12 unframed prints and a pair of 24x32
 * framed ones are not worth the same discount. Reading it here means the sheet
 * displays the same entry checkout will charge.
 *
 * Size handles carry orientation but price does not, so `12-x-9` is tried as
 * `9-x-12` before falling back — otherwise a landscape selection would show a
 * different saving from the portrait one at the same price.
 */
export function savingFor(set, sel) {
  const table = set?.savings_by_selection;
  const size = sel?.size_option;
  const framing = sel?.framing_option;
  if (table && size && framing) {
    const direct = table[`${size}|${framing}`];
    if (direct !== undefined) return direct;
    const dims = String(size).split('-x-');
    if (dims.length === 2) {
      const flipped = table[`${dims[1]}-x-${dims[0]}|${framing}`];
      if (flipped !== undefined) return flipped;
    }
  }
  return set?.savings_cents || 0;
}

export function setTotals(set, sel) {
  let total = 0;
  for (const piece of set.pieces) {
    const t = pieceTotalCents(piece.pricing, sel);
    if (t === null) return { addable: false, total: null, saving: 0, bundle: null };
    total += t;
  }
  // Never let the saving exceed the set: a misconfigured amount should show a
  // free set, not a negative one.
  const saving = Math.min(savingFor(set, sel), total);
  return { addable: true, total, saving, bundle: Math.max(0, total - saving) };
}

class BundleSaveUnit extends HTMLElement {
  #io = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#reportReached();
      whenSkuPickerReady(
        (sku) => this.#boot(sku),
        () => { this.#hideUnit('sku_picker_timeout'); }
      );
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  #controller = new AbortController();

  // The suggestions request, started at connect rather than at intersection.
  // It needs the anchor handle, the cart and the currency — none of which
  // depend on the sku picker or on the shopper having scrolled. Measured
  // 8 Sep 2026: the round trip is p50 3.0s / p90 4.6s, and only ~36% of
  // exposed shoppers were still on the page when the cards finished
  // rendering. Starting it here spends that latency against the time the
  // shopper is reading the buy box instead of against the time they are
  // waiting at the empty slot below it.
  //
  // The observer still decides when to SHOW. Do not widen its rootMargin to
  // chase the same number: bundle_save_viewed fires on render, so rendering
  // further off-screen would raise the metric without another shopper seeing
  // anything.
  #setsPromise = null;
  // performance.now() at which the suggestions resolved, or null while still in
  // flight. Read by #reportReached to say whether the shopper arrived before or
  // after the fetch came back — the one thing the aggregates could not settle.
  #setsResolvedAt = null;
  // Bumped on every #startFetch. #timed only stamps for the CURRENT generation,
  // so a resolved-then-refetched unit reports sets_ready against the promise
  // #boot will actually await, not against a superseded one.
  #fetchGeneration = 0;
  // How far into window.dataLayer a previous #reportPieceAdds has already
  // consumed entries. Two set adds inside the same diff window would otherwise
  // read overlapping slices, and the second could claim the first's embed push
  // as its own — dropping a piece's Meta event.
  #atcDiffCursor = 0;
  // bundle_save_reached is a once-per-element fact, but connectedCallback
  // re-observes on every reconnect (view transition, section morph) and the
  // observer re-arms, so without this a morph would report the same shopper
  // reaching the unit twice.
  #reachedReported = false;
  #booted = false;

  connectedCallback() {
    const qaOverride = new URLSearchParams(location.search).get('bundle_save') === '1';
    if (this.dataset.enabled !== 'true' && !qaOverride) return; // stays hidden
    // disconnectedCallback aborts the controller; listeners added with an
    // already-aborted signal are silent no-ops, so a reconnected element
    // (view transition, morph move) would never hear another event. Make a
    // fresh controller for each connected lifecycle.
    if (this.#controller.signal.aborted) {
      this.#controller = new AbortController();
    }
    // Unhide to a zero-height, contentless block so the IntersectionObserver
    // has a box to observe. Visually renders nothing until #boot populates it.
    this.hidden = false;
    this.#startFetch();
    // A prefetch reads the cart as it was at page load. Someone who adds to
    // cart from the buy box and only then scrolls down would otherwise be
    // offered a print they already hold — re-ask while the unit is still
    // unbooted, which is the only window in which the answer can still change.
    document.addEventListener(
      'cart:update',
      () => {
        if (!this.#booted) this.#startFetch();
      },
      { signal: this.#controller.signal }
    );
    this.#io.observe(this);
  }

  #startFetch() {
    // A new request is in flight, so the previous resolution no longer
    // describes what #boot will await.
    this.#fetchGeneration += 1;
    this.#setsResolvedAt = null;
    // snippets/bundle-save.liquid fires this request during HTML parse, which
    // is several seconds before this module executes on a slow connection.
    // Take that answer if it is there, and clear it so a later cart:update
    // re-ask does not get handed the same stale promise again.
    const inline = window.__fyBundleSets;
    if (inline) {
      window.__fyBundleSets = null;
      this.#setsPromise = this.#timed(inline);
      return;
    }
    // Never let the prefetch reject on its own: nothing is awaiting it yet, and
    // an unhandled rejection here would surface as a page error for a request
    // whose only failure mode is "the unit stays hidden", per the degradation
    // contract.
    this.#setsPromise = this.#timed(this.#fetchSets().catch(() => null));
  }

  /** Stamp #setsResolvedAt when the suggestions land, passing the value through. */
  #timed(promise) {
    const generation = this.#fetchGeneration;
    const stamp = () => {
      if (generation === this.#fetchGeneration) this.#setsResolvedAt = performance.now();
    };
    // Stamp on rejection too. Both current callers pre-catch, so this only
    // matters if that ever changes — but "the fetch came back" is true either
    // way, and a silent never-stamped promise is the failure this exists to
    // prevent. The rejection is re-thrown, not swallowed.
    return Promise.resolve(promise).then(
      (v) => {
        stamp();
        return v;
      },
      (e) => {
        stamp();
        throw e;
      }
    );
  }

  // Split out of #boot so it can run before the sku picker exists. Returns the
  // sets, or null for any failure — the caller hides the unit either way.
  async #fetchSets() {
    const root = window.Shopify?.routes?.root || '/';
    let exclude = '';
    try {
      const cart = await (await fetch(`${root}cart.js`)).json();
      exclude = [...new Set((cart.items || []).map((i) => i.handle).filter(Boolean))].join(',');
    } catch (_) { /* cart read is best-effort */ }
    const params = new URLSearchParams({ anchor: this.dataset.anchorHandle, max: '6' });
    if (exclude) params.set('exclude', exclude);
    // The API prices in the session's currency — without this, non-GB sessions
    // get GBP cents rendered in the local money format.
    const currency = window.Shopify?.currency?.active;
    if (currency) params.set('currency', currency);
    const res = await fetch(`${root}apps/chat-agent/api/fy-set-suggestions?${params}`);
    if (!res.ok) return null;
    const json = await res.json();
    return Array.isArray(json.sets) ? json.sets : [];
  }

  disconnectedCallback() {
    this.#io.disconnect();
    this.#controller.abort();
  }

  #onSelectionChanged = () => {
    if (!this.sets || !this.cardEls) return;
    const sel = this.sku.state.confirmed;
    const mockCfg = this.sku.getResolvedMockConfig() || {};
    const paper = this.#paperDims();
    for (const set of this.sets) {
      this.repriceCard(set, sel);
      const framesEl = this.cardEls.get(set.setId)?.querySelector('[data-bs-frames]');
      if (framesEl) this.#applyCellAspect(framesEl);
      for (const frameEl of this.frameEls.get(set.setId) || []) {
        if (typeof frameEl.updateConfig === 'function') {
          frameEl.updateConfig({
            frameColor: mockCfg.frameColorHex,
            mouldingTile: mockCfg.mouldingTile,
            mouldingWidth: mockCfg.mouldingWidth,
            borderWidth: mockCfg.borderWidth,
            borderOption: mockCfg.borderOption,
            hasFrame: mockCfg.hasFrame,
            artworkWidth: paper.widthInches,
            artworkAspect: paper.aspect,
            productType: mockCfg.productType,
          });
        }
      }
    }
    this.#refreshOpenSheet();
  };

  // The cm | in toggle changes no prices and no mocks — only the numbers we
  // print — so repricing the cards (which rewrites their meta line) and
  // re-rendering an open sheet is the whole job.
  #onUnitChanged = () => {
    if (!this.sets || !this.cardEls) return;
    const sel = this.sku.state.confirmed;
    for (const set of this.sets) this.repriceCard(set, sel);
    this.#refreshOpenSheet();
  };

  // Re-render the sheet in place when it is open. A configurator change must
  // not cost the shopper their open swap tray, so it is reopened with the same
  // candidate pool and rail position.
  #refreshOpenSheet() {
    const dialog = this.refsQ('[data-bs-sheet]');
    if (!dialog?.open || !this.#openWorkingSet) return;
    const tray = this.#openTray;
    this.#renderSheet(this.#openWorkingSet);
    if (!tray) return;
    const rows = this.refsQ('[data-bs-sheet-body]')?.querySelectorAll('.bundle-save__sheet-piece');
    const row = rows?.[tray.index];
    if (row) this.#toggleTray(row, this.#openWorkingSet, tray.index, tray.state);
  }

  i18n(key, vars = {}) {
    let s = this.dataset[key] || '';
    for (const [k, v] of Object.entries(vars)) s = s.replace(`__${k.toUpperCase()}__`, v);
    return s;
  }

  refsQ(sel) {
    return this.querySelector(sel);
  }

  /**
   * Fired the moment the shopper scrolls to the unit — from inside the
   * observer, BEFORE the sku-picker wait and before the suggestions are
   * awaited, so it lands even when nothing ever renders.
   *
   * `bundle_save_viewed` only fires once the unit has actually rendered, so on
   * its own it cannot tell a shopper who never scrolled this far from one who
   * did and got nothing. That ambiguity is what left the US reach shortfall
   * half unexplained: matched-dwell analysis accounted for ~58% of it and the
   * residual (3.9pp mobile, 8.8pp desktop) could not be attributed. With this
   * event, `reached / PDP views` is the scroll rate and `viewed / reached` is
   * the render rate, and the two are finally separable.
   *
   * `sets_ready` is the direct answer to "did they arrive before the fetch
   * came back" — the question the 8 Sep prefetch was meant to settle and
   * could only be argued about from aggregates.
   */
  #reportReached() {
    if (this.#reachedReported) return;
    this.#reachedReported = true;
    if (!window.posthog?.capture) return;
    try {
      window.posthog.capture('bundle_save_reached', {
        anchor: this.dataset.anchorHandle,
        // The observer carries a 400px bottom margin, so this fires shortly
        // BEFORE the unit is on screen, not when it is. Do not read it as
        // "seen" — that is what bundle_save_viewed is for.
        trigger: 'io_rootmargin_400px',
        // Whether the prefetched suggestions had already resolved when the
        // shopper got here. False means they waited; true means the unit was
        // ready and any failure after this point is not latency.
        sets_ready: this.#setsResolvedAt !== null,
        ms_since_nav: Math.round(performance.now()),
      });
    } catch (_) {
      /* reporting must never break the boot */
    }
  }

  // Degradation contract: any failure renders nothing at all — including the
  // loading skeleton, which is torn down rather than just hidden.
  // `reason` is reported so a unit that reached the shopper but never rendered
  // says WHY, rather than being indistinguishable from one never scrolled to.
  #hideUnit(reason) {
    this.hidden = true;
    this.removeAttribute('aria-busy');
    const carousel = this.refsQ('[data-bs-carousel]');
    if (carousel) carousel.textContent = '';
    if (reason && window.posthog?.capture) {
      try {
        window.posthog.capture('bundle_save_hidden', {
          anchor: this.dataset.anchorHandle,
          reason,
          sets_ready: this.#setsResolvedAt !== null,
          ms_since_nav: Math.round(performance.now()),
        });
      } catch (_) {
        /* never break the degradation path for the sake of a metric */
      }
    }
  }

  // Placeholder cards shaped like the real ones, shown from the moment the
  // unit scrolls into view until the suggestions land. The degradation
  // contract still holds — an error or an empty result hides the whole unit —
  // but while the request IS in flight the space is reserved and the section
  // reads as loading instead of popping in under the ATC.
  #renderSkeleton() {
    this.refsQ('[data-bs-heading]').textContent = this.i18n('i18nHeading');
    const carousel = this.refsQ('[data-bs-carousel]');
    carousel.textContent = '';
    for (let i = 0; i < 2; i++) {
      // Deliberately NOT .bundle-save__card: a placeholder must not answer to
      // the real card's selector, or a tap (and every querySelector that means
      // "a set") lands on an empty box.
      const li = document.createElement('li');
      li.className = 'bundle-save__skcard';
      li.setAttribute('aria-hidden', 'true');
      const wall = document.createElement('span');
      wall.className = 'bundle-save__sk bundle-save__sk-wall';
      const name = document.createElement('span');
      name.className = 'bundle-save__sk bundle-save__sk-line';
      const meta = document.createElement('span');
      meta.className = 'bundle-save__sk bundle-save__sk-line bundle-save__sk-line--short';
      const price = document.createElement('span');
      price.className = 'bundle-save__sk bundle-save__sk-line bundle-save__sk-line--price';
      li.append(wall, name, meta, price);
      carousel.appendChild(li);
    }
    this.setAttribute('aria-busy', 'true');
    this.hidden = false;
  }

  async #boot(sku) {
    this.sku = sku;
    this.#booted = true;
    this.#renderSkeleton();
    try {
      // Usually already resolved by now — that is the point. The skeleton only
      // shows for whatever is left of the round trip, not the whole of it.
      // #timed here too, not just in #startFetch: this fallback is the one path
      // that can produce a #setsPromise without one, and an untimed promise
      // leaves #setsResolvedAt null forever, so bundle_save_reached would
      // report sets_ready:false for a unit whose sets had in fact landed.
      const sets = await (this.#setsPromise ??= this.#timed(
        this.#fetchSets().catch(() => null)
      ));
      this.sets = Array.isArray(sets) ? sets : [];
      if (!this.sets.length) {
        this.#hideUnit('no_sets');
        return;
      }
      this.refsQ('[data-bs-heading]').textContent = this.i18n('i18nHeading');
      this.renderCards();
      this.#prewarmRoomLayouts();
      let swipeFired = false;
      this.refsQ('[data-bs-carousel]').addEventListener(
        'scroll',
        () => {
          if (swipeFired) return;
          swipeFired = true;
          if (window.posthog?.capture) {
            window.posthog.capture('bundle_save_card_swiped', {
              anchor: this.dataset.anchorHandle,
            });
          }
        },
        { passive: true, signal: this.#controller.signal }
      );
      this.refsQ('[data-bs-sheet-close]')?.addEventListener(
        'click',
        () => this.refsQ('[data-bs-sheet]').close(),
        { signal: this.#controller.signal }
      );
      // Tap-outside-to-close. <dialog> has no built-in backdrop-click
      // behaviour — a click anywhere gets delivered to whatever element sits
      // under it, backdrop included. Position, not target: the dialog's own
      // padding is 0, so a click landing outside its rendered box is the
      // backdrop, no matter what the browser resolves target to.
      this.refsQ('[data-bs-sheet]')?.addEventListener(
        'click',
        (e) => {
          const dialog = e.currentTarget;
          const r = dialog.getBoundingClientRect();
          const inside =
            e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
          if (!inside) dialog.close();
        },
        { signal: this.#controller.signal }
      );
      this.refsQ('[data-bs-sheet]')?.addEventListener(
        'close',
        () => {
          this.#openWorkingSet = null;
          this.#openTray = null;
          document.documentElement.classList.remove('bundle-save-sheet-open');
        },
        { signal: this.#controller.signal }
      );
      // The awaited fetches above give the element time to be disconnected
      // (e.g. a section morph) before we get here — don't register a
      // listener nobody will clean up.
      if (!this.isConnected) return;
      document.addEventListener('sku-picker:selection-changed', this.#onSelectionChanged, {
        signal: this.#controller.signal,
      });
      document.addEventListener('sku-picker:unit-changed', this.#onUnitChanged, {
        signal: this.#controller.signal,
      });
      this.hidden = false;
      if (window.posthog?.capture) {
        window.posthog.capture('bundle_save_viewed', {
          anchor: this.dataset.anchorHandle,
          set_count: this.sets.length,
        });
      }
    } catch (_) {
      // Degradation contract: render nothing.
      this.#hideUnit('render_error');
    }
  }

  // Every piece in a set renders at the CONFIGURED print size, so the paper
  // aspect comes from the selected size dimensions — not from the piece's
  // source-image aspect (drawImageCover cover-fits the artwork into the paper).
  #paperDims() {
    const d = this.sku.getSizeDimensions();
    return {
      widthInches: d?.widthInches || 16,
      aspect: d?.widthInches && d?.heightInches ? d.widthInches / d.heightInches : 0.75,
    };
  }

  // The confirmed frame colour record (label, short_name, …), or null when the
  // selection is unframed / no colour is confirmed yet.
  #selectedFrameColour() {
    const sel = this.sku.state.confirmed;
    const framing = (this.sku.data?.options?.framing || []).find(
      (f) => f.handle === sel.framing_option
    );
    return framing?.colors?.find((c) => c.handle === sel.frame_color) || null;
  }

  // The in-room slide is a PHOTOGRAPH: its frames are part of the picture and
  // cannot be recoloured, so it may only be shown when the depicted frame
  // colour is the one the shopper picked. Unframed selections (the PDP
  // default) have no honest room photo at all — the slide is omitted and the
  // parametric wall, which does follow the selection, leads the gallery.
  #roomTemplate(layout) {
    if (!(this.sku.getResolvedMockConfig() || {}).hasFrame) return null;
    const key = (this.#selectedFrameColour()?.short_name || '').trim().toLowerCase();
    if (!key) return null;
    return this.querySelector(
      `[data-bs-mock-template="${layout}"][data-bs-mock-frame="${key}"]`
    );
  }

  // The wall composited into a real room. Frames are rendered by
  // <room-background-mock> (the same component the cart row and the builder
  // use), which listens for `gallery-wall:config-changed` — the event the
  // sku-picker already broadcasts — so the frames track the PDP selection.
  //
  // Room backgrounds are keyed on LAYOUT only, so unlike the photographed
  // lifestyle mocks this covers every frame colour and size with no per-colour
  // content to shoot or publish.
  #buildRoomComposite(set) {
    const el = document.createElement('room-background-mock');
    el.className = 'bundle-save__room';
    el.dataset.layoutHandle = WALL_LAYOUTS[set.layout] || set.layout;
    el.dataset.artworkUrls = set.pieces.map((p) => p.imageUrl).filter(Boolean).join('|||');
    // Position ids map artworks onto frames; the canonical layouts number
    // their positions from 1 in render order.
    el.dataset.positions = set.pieces.map((_, i) => i + 1).join(',');
    // The line's CURRENT framing, the same override channel the cart row uses.
    // The mock otherwise takes its frame from the layout's preset and only
    // follows the selection via live config-changed broadcasts — events that
    // fired BEFORE this element existed are gone, so a colour picked on the
    // PDP before opening the sheet never reached the lead slide (QA: swatch
    // and thumbnails right, mock wrong).
    const sel = this.sku.state.confirmed;
    if (sel.frame_color) el.dataset.frameColorHandle = sel.frame_color;
    if (sel.framing_option) el.dataset.framingOption = sel.framing_option;
    if (sel.border_option) el.dataset.borderOption = sel.border_option;
    // The wall's geometry is OURS, not the layout's. The canonical layout's
    // preset positions are catalogue-sized (a pair of ~47in prints), and the
    // sku-picker's size broadcasts describe this PDP's SINGLE print — applied
    // to a multi-print wall they shrank layoutWidth under unshrunk positions,
    // so the sheet opened oversized and collapsed to gapless miniatures on the
    // first configurator change. Own-geometry hands the mock every print at
    // the SELECTED size on the same size-scaled hanging gap (hangGapInches)
    // the spec line's wall-total arithmetic uses; the sheet re-renders on
    // every selection change, so a size change rebuilds this element with
    // fresh numbers.
    const d = this.sku.getSizeDimensions();
    if (d?.widthInches && d?.heightInches) {
      const [c, r] = this.#gridFor(set.layout, set.pieces.length);
      const G = hangGapInches(d.widthInches, d.heightInches);
      el.dataset.ownGeometry = JSON.stringify({
        positions: set.pieces.map((_, i) => ({
          id: i + 1,
          x: (i % c) * (d.widthInches + G),
          y: Math.floor(i / c) * (d.heightInches + G),
          width: d.widthInches,
          height: d.heightInches,
        })),
        widthInches: c * d.widthInches + (c - 1) * G,
        heightInches: r * d.heightInches + (r - 1) * G,
      });
    }
    return el;
  }

  // One layouts fetch serves every layout and is shared process-wide via
  // window.__layoutsFetch. Cold it costs ~0.8–2.4s to first paint; warm, 53ms.
  // Priming it at boot means the sheet's lead visual is warm by the time
  // anyone opens it.
  #prewarmRoomLayouts() {
    customElements
      .whenDefined('room-background-mock')
      .then(() => customElements.get('room-background-mock')?.fetchLayouts?.())
      .catch(() => { /* the composite still renders, just cold */ });
  }

  #buildFrameEl(piece, mockCfg, paper, positionId) {
    const el = document.createElement('gallery-wall-frame-mock');
    el.className = 'gallery-wall-frame-mock bundle-save__frame';
    el.dataset.artworkUrl = piece.imageUrl;
    el.dataset.artworkTitle = piece.title || 'Artwork';
    el.dataset.positionId = positionId;
    el.dataset.frameColor = mockCfg.frameColorHex || '#2d2d2d';
    if (mockCfg.mouldingTile) el.dataset.mouldingTile = mockCfg.mouldingTile;
    el.dataset.mouldingWidth = mockCfg.mouldingWidth ?? 15;
    el.dataset.borderWidth = mockCfg.borderWidth ?? 0;
    el.dataset.borderOption = mockCfg.borderOption || 'no-border';
    el.dataset.hasFrame = String(mockCfg.hasFrame ?? true);
    el.dataset.artworkWidth = paper.widthInches;
    el.dataset.artworkAspect = paper.aspect;
    el.dataset.productType = mockCfg.productType || 'art_print';
    // Tighter than the 0.80 default: card tiles are small, and real gallery
    // walls hang closer than 20%-of-tile gutters.
    el.dataset.frameFraction = '0.94';
    // Flat wall, no texture/lighting map: adjacent tiles share one colour and
    // blend into a single continuous wall behind the whole set.
    el.dataset.wallColor = '#f5f0e8';
    const canvas = document.createElement('canvas');
    canvas.className = 'gallery-wall-frame-mock__canvas';
    el.appendChild(canvas);
    return el;
  }

  // Wall arrangement per layout: [columns, rows]. Fallback derives a near-
  // square grid so unknown wall-N layouts still compose.
  static #GRID_BY_LAYOUT = {
    'pair-2': [2, 1],
    'trio-3': [3, 1],
    'quad-2x2': [2, 2],
    'salon-5': [3, 2],
    'salon-6': [3, 2],
  };

  #gridFor(layout, pieceCount) {
    const cols = Math.ceil(Math.sqrt(pieceCount));
    return BundleSaveUnit.#GRID_BY_LAYOUT[layout] || [cols, Math.ceil(pieceCount / cols)];
  }

  #applyWallGrid(framesEl, layout, pieceCount) {
    const [c, r] = this.#gridFor(layout, pieceCount);
    framesEl.dataset.layout = layout;
    framesEl.dataset.rows = r;
    framesEl.style.setProperty('--bs-cols', c);
    framesEl.style.setProperty('--bs-rows', r);
    this.#applyCellAspect(framesEl);
  }

  // The frame-mock canvas is square; a portrait print inside leaves wide
  // wall-only margins that read as huge gaps between artworks. Each frame
  // sits in a paper-aspect cell that clips the square's surplus wall (all
  // tiles share one flat wall colour, so the clip is invisible) — art packs
  // to a small, real hanging gap regardless of paper shape.
  #applyCellAspect(framesEl) {
    const cell = Math.min(1, 0.94 * this.#paperDims().aspect + 0.08);
    framesEl.style.setProperty('--bs-cell', cell.toFixed(3));
  }

  // "4 framed prints · 20×26\u2033 wall" — overall hung-wall span at the
  // configured print size: grid spans plus hangGapInches's size-scaled gap between frames.
  //
  // Every dimension the unit prints follows the PDP's cm | in toggle. The
  // sku-picker holds the preference (`currentUnit`) and keeps it in sync with
  // every toggle on the page via sku-picker:unit-changed — a card reading
  // 50×32″ while the page is set to cm is simply the wrong number.
  #inCm() {
    return this.sku.currentUnit === 'cm';
  }

  #trim(value) {
    return String(Math.round(parseFloat(value) * 10) / 10).replace(/\.0$/, '');
  }

  // The configured print size, taken from the size option's OWN figures so cm
  // reads as the catalogue's 30×40, not a converted 30.5×40.6.
  #printSize() {
    const opt = (this.sku.data?.options?.size || []).find(
      (s) => s.handle === this.sku.state.confirmed.size_option
    );
    if (!opt) return '';
    return this.#inCm()
      ? `${this.#trim(opt.width_cms)}×${this.#trim(opt.height_cms)}cm`
      : `${this.#trim(opt.width_inches)}×${this.#trim(opt.height_inches)}″`;
  }

  // A hung-wall span is derived, not catalogued, so it is computed in inches
  // (the unit the grid maths uses) and converted for display.
  #wallSpan(widthInches, heightInches) {
    return this.#inCm()
      ? `${Math.round(widthInches * 2.54)}×${Math.round(heightInches * 2.54)}cm`
      : `${Math.round(widthInches)}×${Math.round(heightInches)}″`;
  }

  #metaText(set) {
    const paper = this.#paperDims();
    const heightInches = paper.widthInches / paper.aspect;
    const [c, r] = this.#gridFor(set.layout, set.pieces.length);
    const gap = hangGapInches(paper.widthInches, heightInches);
    const dims = this.#wallSpan(
      c * paper.widthInches + (c - 1) * gap,
      r * heightInches + (r - 1) * gap
    );
    const framed = this.sku.getResolvedMockConfig()?.hasFrame ?? true;
    return this.i18n(framed ? 'i18nPiecesMetaFramed' : 'i18nPiecesMetaUnframed', {
      count: set.pieces.length,
      dims,
    });
  }

  // Drops `node` into the slot the locale string puts {{ name }} in, so word
  // order stays the translator's ("Black frame" / "cadre noir"). SPEC_SLOT is
  // U+0000, which cannot occur in copy — a safe split marker.
  #interleave(frag, template, node) {
    const [before, after = ''] = template.split(SPEC_SLOT);
    if (before) frag.appendChild(document.createTextNode(before));
    frag.appendChild(node);
    if (after) frag.appendChild(document.createTextNode(after));
  }

  // Applies a configurator change made from inside the sheet. Same path the
  // PDP's own controls take, so the picker above repaints and everything that
  // listens for selection-changed (cards, sheet, mocks) follows.
  #applySelection(patch) {
    Object.assign(this.sku.state.confirmed, patch);
    this.sku._updateConfirmedSelection(true);
  }

  // Frame control: every colour across every framing option, grouped by frame
  // family, plus the unframed options. QA had shoppers scrolling back up the
  // PDP to change frame mid-review — this keeps them in the sheet.
  #frameControl() {
    const sel = this.sku.state.confirmed;
    const select = document.createElement('select');
    select.className = 'bundle-save__spec-select';
    select.setAttribute('data-bs-frame-select', '');
    select.setAttribute('aria-label', this.i18n('i18nSpecFrameLabel'));
    for (const framing of this.sku.data?.options?.framing || []) {
      const label = framing.label || framing.name || framing.handle;
      if (framing.colors?.length) {
        const group = document.createElement('optgroup');
        group.label = label;
        for (const colour of framing.colors) {
          const opt = document.createElement('option');
          opt.value = `${framing.handle}|${colour.handle}`;
          opt.textContent = colour.short_name || colour.label || colour.handle;
          opt.selected =
            framing.handle === sel.framing_option && colour.handle === sel.frame_color;
          group.appendChild(opt);
        }
        select.appendChild(group);
      } else {
        const opt = document.createElement('option');
        opt.value = `${framing.handle}|`;
        opt.textContent = label;
        opt.selected = framing.handle === sel.framing_option;
        select.appendChild(opt);
      }
    }
    if (!select.options.length) return null;
    select.addEventListener('change', (e) => {
      e.stopPropagation();
      const [framingOption, frameColor] = select.value.split('|');
      this.#applySelection({ framing_option: framingOption, frame_color: frameColor || null });
    });
    return select;
  }

  // Mount/border control. Only meaningful on framed selections — an unframed
  // print has no mount to pick.
  #mountControl() {
    const sel = this.sku.state.confirmed;
    const borders = this.sku.data?.options?.border || [];
    if (borders.length < 2) return null;
    const select = document.createElement('select');
    select.className = 'bundle-save__spec-select';
    select.setAttribute('data-bs-mount-select', '');
    select.setAttribute('aria-label', this.i18n('i18nSpecMountLabel'));
    for (const border of borders) {
      const opt = document.createElement('option');
      opt.value = border.handle;
      opt.textContent = border.label || border.handle;
      opt.selected = border.handle === sel.border_option;
      select.appendChild(opt);
    }
    select.addEventListener('change', (e) => {
      e.stopPropagation();
      this.#applySelection({ border_option: select.value });
    });
    return select;
  }

  // Size control: the third in-place select (QA follow-up to frame and mount —
  // size was the one spec segment still read-only). Labels follow the cm | in
  // toggle like every other dimension the unit prints.
  #sizeControl() {
    const sel = this.sku.state.confirmed;
    const sizes = this.sku.data?.options?.size || [];
    if (sizes.length < 2) return null;
    const select = document.createElement('select');
    select.className = 'bundle-save__spec-select';
    select.setAttribute('data-bs-size-select', '');
    select.setAttribute('aria-label', this.i18n('i18nSpecSizeLabel'));
    for (const size of sizes) {
      const opt = document.createElement('option');
      opt.value = size.handle;
      opt.textContent = this.#inCm()
        ? `${this.#trim(size.width_cms)}×${this.#trim(size.height_cms)}cm`
        : `${this.#trim(size.width_inches)}×${this.#trim(size.height_inches)}″`;
      opt.selected = size.handle === sel.size_option;
      select.appendChild(opt);
    }
    select.addEventListener('change', (e) => {
      e.stopPropagation();
      const patch = { size_option: select.value };
      // The PDP's size picker applies these same guards on selection: a size
      // that can't take the current framing falls back to unframed, and a
      // mount border needs a frame. Skipping them here would confirm a
      // combination the configurator itself refuses to produce.
      if (!this.sku.components?.framing?.[select.value]?.[sel.framing_option]) {
        patch.framing_option = 'unframed';
        patch.frame_color = null;
        if (sel.border_option === 'mount') patch.border_option = 'no-border';
      }
      const border = patch.border_option || sel.border_option;
      if (!this.sku.components?.border?.[select.value]?.[border]) {
        patch.border_option = 'no-border';
      }
      this.#applySelection(patch);
    });
    return select;
  }

  // One-line summary of the PDP selection (wireframe v6), with the frame and
  // mount segments editable in place:
  // "3 Fine Art Prints · 21×30″ (total wall size 40×26″) · ⚫ [Black ▾] frame · [White ▾] mount"
  // Segments whose label can't be resolved are dropped rather than guessed.
  #sheetSpecLine(set) {
    const frag = document.createDocumentFragment();
    const sep = () => document.createTextNode(' · ');

    frag.appendChild(
      document.createTextNode(this.i18n('i18nSpecPrints', { count: set.pieces.length }))
    );

    const d = this.sku.getSizeDimensions();
    if (d?.widthInches && d?.heightInches) {
      const [c, r] = this.#gridFor(set.layout, set.pieces.length);
      const gap = hangGapInches(d.widthInches, d.heightInches);
      const dims = this.#wallSpan(
        c * d.widthInches + (c - 1) * gap,
        r * d.heightInches + (r - 1) * gap
      );
      // Wireframe v6: print size in ink weight, wall total muted parenthetical —
      // separate elements so the CSS distinction can actually render. The size
      // itself is editable in place (QA follow-up: frame and mount already
      // were); single-size products keep the static text.
      const sizeSelect = this.#sizeControl();
      const sizeEl = document.createElement('strong');
      if (sizeSelect) {
        sizeEl.appendChild(sizeSelect);
      } else {
        sizeEl.textContent = this.#printSize();
      }
      const wallEl = document.createElement('span');
      wallEl.className = 'bundle-save__spec-muted';
      wallEl.textContent = this.i18n('i18nSpecWallTotal', { dims });
      frag.append(sep(), sizeEl, document.createTextNode(' '), wallEl);
    }

    const mockCfg = this.sku.getResolvedMockConfig() || {};
    const frameSelect = this.#frameControl();
    if (frameSelect) {
      frag.append(sep());
      if (mockCfg.hasFrame) {
        // "⚫ [Black ▾] frame". Unframed selections carry no swatch and no
        // trailing noun — the option already reads "Unframed".
        const swatch = document.createElement('span');
        swatch.className = 'bundle-save__spec-swatch';
        swatch.style.background = mockCfg.frameColorHex || '#2d2d2d';
        frag.append(swatch, document.createTextNode(' '));
        this.#interleave(frag, this.i18n('i18nSpecFrame', { name: SPEC_SLOT }), frameSelect);
      } else {
        frag.append(frameSelect);
      }
    }
    if (mockCfg.hasFrame) {
      const mountSelect = this.#mountControl();
      if (mountSelect) {
        // No trailing noun: the border options already name themselves ("White
        // Mount", "No Border"), and appending one reads as "No Border mount".
        frag.append(sep(), mountSelect);
      }
    }
    return frag;
  }

  // The working copy behind the open sheet, so a configurator change made
  // anywhere (the in-sheet selects, the PDP picker, the unit toggle) can
  // re-render it.
  #openWorkingSet = null;

  // Gallery slide the shopper is on, preserved across re-renders.
  #activeSlideKey = null;

  // { index, state } of the open swap tray, so a re-render can restore it.
  #openTray = null;

  #openSheet(set) {
    // The sheet works on a COPY: swaps customise this open's session without
    // mutating the canonical curated set behind the carousel card (reopening
    // resets to the curation; the add CTA posts whatever the copy holds).
    const working = { ...set, pieces: [...set.pieces] };
    this.#openWorkingSet = working;
    this.#activeSlideKey = null; // a fresh open starts on the leading slide
    this.#renderSheet(working);
    const dialog = this.refsQ('[data-bs-sheet]');
    dialog.showModal();
    // iOS Safari (and Chrome on Android) still scroll the page behind a modal
    // <dialog>; the UA's inertness covers pointer events, not scrolling.
    document.documentElement.classList.add('bundle-save-sheet-open');
    // showModal() lands focus on the first focusable child — the × — so the
    // sheet opened with a focus ring drawn round the close button. Park focus
    // on the dialog itself (tabindex="-1"); the modal still traps focus and the
    // first Tab still reaches the close button.
    dialog.focus();
    if (window.posthog?.capture) {
      window.posthog.capture('bundle_save_sheet_opened', {
        anchor: this.dataset.anchorHandle, set_id: set.setId, layout: set.layout,
      });
    }
  }

  // Fills every sheet region from `set`. Called on open and again after a
  // swap — never calls showModal, so it is safe while the dialog is open.
  #renderSheet(set) {
    const dialog = this.refsQ('[data-bs-sheet]');
    const sel = this.sku.state.confirmed;
    const { addable, total, saving, bundle } = setTotals(set, sel);

    this.refsQ('[data-bs-sheet-name]').textContent =
      set.name || this.i18n('i18nFallbackName', { count: set.pieces.length });
    const spec = this.refsQ('[data-bs-sheet-spec]');
    spec.textContent = '';
    spec.appendChild(this.#sheetSpecLine(set));

    const gallery = this.refsQ('[data-bs-sheet-gallery]');
    gallery.textContent = '';
    gallery.appendChild(this.#buildGallery(set)); // Task 3 replaces this stub

    const body = this.refsQ('[data-bs-sheet-body]');
    body.textContent = '';
    const list = document.createElement('ul');
    list.className = 'bundle-save__sheet-pieces';
    set.pieces.forEach((piece, i) => list.appendChild(this.#buildPieceRow(piece, sel, set, i)));
    body.appendChild(list);

    const footer = this.refsQ('[data-bs-sheet-footer]');
    footer.textContent = '';
    const priceRow = document.createElement('p');
    priceRow.className = 'bundle-save__price';
    if (addable) {
      // The bundle price already leads the CTA ("Add set to cart · £100.80"),
      // so this row carries only what the button can't: what you'd otherwise
      // pay, and what you save. One line less on a cramped mobile sheet.
      const strike = document.createElement('s');
      strike.textContent = this.sku._formatPrice(total);
      priceRow.append(strike);
      if (saving > 0) {
        const badge = document.createElement('span');
        badge.className = 'bundle-save__badge bundle-save__badge--inline';
        badge.textContent = this.i18n('i18nSaveBadge', {
          amount: this.sku._formatPrice(saving),
        });
        priceRow.appendChild(badge);
      }
    } else {
      const unavailable = document.createElement('strong');
      unavailable.textContent = this.i18n('i18nUnavailable');
      priceRow.appendChild(unavailable);
    }
    footer.appendChild(priceRow);

    const add = document.createElement('button');
    add.type = 'button';
    add.className = 'bundle-save__add button';
    add.setAttribute('data-bs-add', '');
    const alreadyAdded = this.#addedSets.has(this.#setSignature(set));
    // PDP ATC parity: "Add set to cart · £19.90". One text node — the theme
    // .button class lays out children as flex items, wrapping a price span
    // onto its own line.
    add.textContent =
      !alreadyAdded && addable
        ? `${this.i18n('i18nAddSet')} · ${this.sku._formatPrice(bundle)}`
        : this.i18n(alreadyAdded ? 'i18nSetAdded' : 'i18nAddSet');
    add.disabled = !addable || alreadyAdded;
    if (alreadyAdded) add.classList.add('is-added');
    add.addEventListener('click', async () => {
      await this.#addSet(set, add);
      // Sheet must be closed before the cart drawer (also a modal <dialog>) opens.
      if (add.classList.contains('is-added')) dialog.close();
    });
    footer.appendChild(add);
  }

  // Gallery: swipeable snap-scroll hero track + image thumbnails. Slides, in
  // order: in-room lifestyle (when a mock template exists for the layout),
  // the 2D parametric wall (size/frame-true), then one framed close-up per
  // piece. All slides are built at open so swiping is native scroll; thumbs
  // are real images (room photo / mini piece grid / artworks) and sync with
  // the scroll position.
  #buildGallery(set) {
    const wrap = document.createElement('div');
    wrap.className = 'bundle-save__gallery';
    const track = document.createElement('div');
    track.className = 'bundle-save__hero-track';
    track.setAttribute('data-bs-hero', '');
    const rail = document.createElement('div');
    rail.className = 'bundle-save__thumbs';
    wrap.append(track, rail);

    const mockCfg = this.sku.getResolvedMockConfig() || {};
    const paper = this.#paperDims();
    const tpl = this.#roomTemplate(set.layout);

    const slides = [];
    // Lead slide: the wall composited into a real room — an empty-wall room
    // photograph with the frames RENDERED over it. Because the frames are
    // drawn rather than photographed they follow the configurator exactly, so
    // this one slide covers every layout × frame colour × size. It replaces
    // the flat parametric composite, which showed the same information without
    // the room, and it demotes the lifestyle photo below (see #roomTemplate:
    // that one is a photograph of specific frames and can only be shown for
    // the colours someone has shot and published).
    {
      const slide = document.createElement('div');
      slide.className = 'bundle-save__slide bundle-save__slide-composite';
      slide.appendChild(this.#buildRoomComposite(set));
      slides.push({
        key: 'wall',
        el: slide,
        label: this.i18n('i18nSlideWall'),
        thumbGrid: set.pieces,
        thumbLayout: this.#gridFor(set.layout, set.pieces.length),
      });
    }
    if (tpl) {
      const holder = document.createElement('div');
      holder.className = 'bundle-save__slide bundle-save__slide-room';
      holder.appendChild(tpl.content.cloneNode(true));
      const mock = holder.querySelector('art-set-mock');
      if (mock) {
        // Attribute, not property: art-set-mock's connectedCallback re-parses
        // data-artworks, so a property set pre-connect would be clobbered.
        mock.dataset.artworks = JSON.stringify(
          set.pieces.map((p) => ({ url: p.imageUrl, title: p.title }))
        );
      }
      slides.push({
        key: 'room',
        el: holder,
        label: this.i18n('i18nSlideRoom'),
        // Live mini mock: the raw lifestyle photo has EMPTY frames (artworks
        // are warped in by JS), so a static img thumb reads as a bare room.
        thumbLive: tpl,
        thumbArtworks: set.pieces,
      });
    }
    set.pieces.forEach((piece, i) => {
      const solo = document.createElement('div');
      solo.className = 'bundle-save__slide bundle-save__slide-solo';
      solo.appendChild(this.#buildFrameEl(piece, mockCfg, paper, `bs-solo-${set.setId}-${i}`));
      slides.push({ key: `piece-${i}`, el: solo, label: piece.title || '', thumbSrc: piece.imageUrl || null });
    });

    const setActive = (idx) => {
      [...rail.children].forEach((btn, i) => btn.setAttribute('aria-pressed', String(i === idx)));
      this.#activeSlideKey = slides[idx]?.key || this.#activeSlideKey;
    };
    slides.forEach((slide, idx) => {
      track.appendChild(slide.el);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bundle-save__gthumb';
      btn.dataset.bsSlide = slide.key;
      btn.setAttribute('aria-label', slide.label);
      btn.setAttribute('aria-pressed', 'false');
      if (slide.thumbLive) {
        const live = document.createElement('span');
        live.className = 'bundle-save__gthumb-live';
        live.appendChild(slide.thumbLive.content.cloneNode(true));
        const mock = live.querySelector('art-set-mock');
        if (mock) {
          mock.dataset.artworks = JSON.stringify(
            slide.thumbArtworks.map((p) => ({ url: p.imageUrl, title: p.title }))
          );
        }
        btn.appendChild(live);
      } else if (slide.thumbGrid) {
        const grid = document.createElement('span');
        grid.className = 'bundle-save__gthumb-grid';
        // Mirror the REAL wall layout (trio = 3×1, salon-6 = 3×2 …) with
        // explicit fr tracks: auto tracks make the imgs' percentage heights
        // unresolvable and the artwork renders stretched.
        const [tc, tr] = slide.thumbLayout;
        grid.style.gridTemplateColumns = `repeat(${tc}, 1fr)`;
        grid.style.gridTemplateRows = `repeat(${tr}, 1fr)`;
        const shown = slide.thumbGrid.slice(0, tc * tr);
        shown.forEach((p) => {
          const im = document.createElement('img');
          im.loading = 'lazy';
          im.alt = '';
          if (p.imageUrl) im.src = p.imageUrl;
          grid.appendChild(im);
        });
        btn.appendChild(grid);
      } else if (slide.thumbSrc) {
        btn.classList.add('bundle-save__gthumb--art');
        const im = document.createElement('img');
        im.loading = 'lazy';
        im.alt = '';
        im.src = slide.thumbSrc;
        btn.appendChild(im);
      }
      btn.addEventListener('click', () => {
        track.scrollTo({ left: idx * track.clientWidth, behavior: 'smooth' });
        setActive(idx);
        if (window.posthog?.capture) {
          window.posthog.capture('bundle_save_gallery_slide', {
            anchor: this.dataset.anchorHandle, set_id: set.setId, slide: slide.key,
          });
        }
      });
      rail.appendChild(btn);
    });
    // Swipe sync: track scroll position drives the active thumb.
    let scrollRaf = 0;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(scrollRaf);
      scrollRaf = requestAnimationFrame(() => {
        setActive(Math.round(track.scrollLeft / Math.max(1, track.clientWidth)));
      });
    }, { passive: true });

    // Hold the slide the shopper was looking at across a re-render. Changing
    // frame colour rebuilds the gallery, and jumping back to slide 0 — or to a
    // room mock that only exists for some colours — makes it impossible to
    // compare two frames on the same image, which is the whole point of the
    // in-sheet frame control.
    let start = slides.findIndex((s) => s.key === this.#activeSlideKey);
    if (start < 0) start = 0;
    setActive(start);
    if (start > 0) {
      // The track has no width until it is laid out inside the open dialog.
      requestAnimationFrame(() => {
        track.scrollTo({ left: start * track.clientWidth, behavior: 'auto' });
      });
    }
    return wrap;
  }

  // The PDP URL for a piece, carrying the sheet's configuration so the product
  // page opens on the same size/frame/mount instead of resetting to a bare
  // print. Param names are the sku-picker's own (_updateURL / _getStateFromURL)
  // — `frame-color`, not `frame_color`.
  #pieceUrl(piece) {
    const sel = this.sku.state.confirmed;
    const params = new URLSearchParams();
    if (sel.size_option) params.set('size', sel.size_option);
    if (sel.framing_option) params.set('framing', sel.framing_option);
    if (sel.border_option) params.set('border', sel.border_option);
    if (sel.frame_color && sel.framing_option !== 'unframed') {
      params.set('frame-color', sel.frame_color);
    }
    const root = window.Shopify?.routes?.root || '/';
    const query = params.toString();
    return `${root}products/${piece.handle}${query ? `?${query}` : ''}`;
  }

  #buildPieceRow(piece, sel, set, index) {
    const item = document.createElement('li');
    item.className = 'bundle-save__sheet-piece';

    const thumb = document.createElement('img');
    thumb.className = 'bundle-save__piece-thumb';
    thumb.loading = 'lazy';
    thumb.alt = '';
    if (piece.imageUrl) thumb.src = piece.imageUrl;
    // Box follows the piece's own shape (the API already returns it as
    // `aspect`), not a fixed portrait ratio — a landscape or square piece
    // was previously center-cropped into the same tall box as a portrait
    // one, which reads as a wrong crop, not a thumbnail of that artwork.
    thumb.style.setProperty('--bs-piece-aspect', piece.aspect || 0.75);
    // Frame-echo follows the selection: a fixed dark border read as a black
    // frame while the shopper had White or Gold picked.
    const thumbCfg = this.sku.getResolvedMockConfig() || {};
    if (thumbCfg.hasFrame) {
      thumb.classList.add('bundle-save__piece-thumb--framed');
      thumb.style.setProperty('--bs-thumb-frame', thumbCfg.frameColorHex || '#2d2d2d');
    }

    const who = document.createElement('span');
    who.className = 'bundle-save__piece-who';
    const link = document.createElement('a');
    link.className = 'bundle-save__piece-link';
    link.href = this.#pieceUrl(piece);
    link.target = '_blank';
    link.rel = 'noopener';
    // Exploration must not lose the set: new tab, and the click must not
    // bubble into anything that closes the dialog.
    link.addEventListener('click', (e) => e.stopPropagation());
    link.textContent = piece.title || '';
    const artistEl = document.createElement('span');
    artistEl.className = 'bundle-save__sheet-artist';
    artistEl.textContent = piece.artist || '';
    // No per-row configuration restatement: every piece shares the sheet's one
    // selection, which the spec line above already states (and now lets you
    // change) — repeating it per row only added a clipped duplicate.
    who.append(link, artistEl);

    const ops = document.createElement('span');
    ops.className = 'bundle-save__piece-ops';
    const swap = document.createElement('button');
    swap.type = 'button';
    swap.className = 'bundle-save__swap-link';
    swap.setAttribute('data-bs-swap', '');
    swap.textContent = this.i18n('i18nSwap');
    swap.addEventListener('click', (e) => {
      e.stopPropagation();
      this.#toggleTray(item, set, index);
    });
    ops.appendChild(swap);
    const heart = document.createElement('button');
    heart.type = 'button';
    heart.className = 'bundle-save__heart';
    heart.setAttribute('data-bs-heart', '');
    heart.setAttribute('aria-label', this.i18n('i18nHeart'));
    heart.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>';
    const paint = () => {
      const liked = WishlistState.isLiked(piece.handle);
      heart.setAttribute('aria-pressed', String(liked));
      heart.classList.toggle('is-liked', liked);
    };
    paint();
    heart.addEventListener('click', (e) => {
      e.stopPropagation();
      WishlistState.toggle(piece.handle, piece.productId);
      paint();
      if (window.posthog?.capture) {
        window.posthog.capture('bundle_save_piece_hearted', {
          anchor: this.dataset.anchorHandle, artwork: piece.handle,
          liked: heart.getAttribute('aria-pressed') === 'true',
        });
      }
    });
    const cents = pieceTotalCents(piece.pricing, sel);
    const priceEl = document.createElement('span');
    priceEl.className = 'bundle-save__piece-price';
    priceEl.textContent = cents === null ? '—' : this.sku._formatPrice(cents);
    ops.append(heart, priceEl);

    item.append(thumb, who, ops);
    return item;
  }

  // Cart-line properties that describe the SELECTION and are therefore valid
  // on every piece of a set — read off the PDP's own `properties[...]` hidden
  // inputs so a set's lines carry exactly what a single add would.
  //
  // This is an allowlist, deliberately. Copying the form wholesale also copies
  // properties that belong to the ANCHOR, not the selection, and on a
  // personalisable PDP that includes the shopper's own uploaded artwork
  // (`sku-picker.js` `PERSONALISE_PROPERTY_KEYS`: `_design_id`,
  // `_photo_s3_key`, `_rendered_image_url`, ...). Stamping those onto a
  // companion's line would attach one shopper's photo to someone else's
  // artwork. `_mock_image_handle` is excluded for a quieter reason: it is
  // resolved against the anchor product's own mock templates, so a companion
  // without that template would render the wrong thumbnail — the cart falls
  // back to the configurator's default mock, which sku-picker itself calls
  // "safer than a wrong handle".
  static SHARED_PROPERTY_KEYS = [
    '_size_option',
    '_framing_option',
    '_border_option',
    '_frame_color',
    '_prodigi_sku',
    '_prodigi_color',
    '_prodigi_wrap',
    '_border_percent',
    'Size',
    'Frame',
    'Format',
    'Upgrades',
  ];

  #pdpProperties() {
    const out = {};
    const form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return out;
    for (const key of BundleSaveUnit.SHARED_PROPERTY_KEYS) {
      const input = form.querySelector(`input[name="properties[${key}]"]`);
      if (input && input.value !== '') out[key] = input.value;
    }
    return out;
  }

  // Whether the anchor's current selection is a paper art print.
  //
  // The suggestions API only ever offers art-print companions, and the Prodigi
  // fields above are looked up against the ANCHOR's `art_product`. On a canvas
  // selection those would be canvas SKUs stamped onto print lines, so the set
  // is not addable — better no offer than a mis-fulfilled one.
  #anchorIsArtPrint() {
    const variant = this.sku.variantConfigurators?.variants?.[this.sku.currentVariantId];
    return variant?.artProduct !== 'canvas-print';
  }

  // Persists the working wall through the walls API and returns
  // { shortId, layoutHandle }, or null if anything fails.
  //
  // Two callers need it: the advisor handoff (which lands on
  // /a/advisor/w/<shortId>) and add-to-cart (which stamps the short id onto
  // every line so the cart's bundle card can reopen the real wall in the
  // builder — without it the builder opens the right layout with empty frames).
  async #saveWall(set) {
    const root = window.Shopify?.routes?.root || '/';
    try {
      const { layouts } = await (await fetch(`${root}a/advisor/api/layouts`)).json();
      const fits = (l) =>
        l.frameCount === set.pieces.length && l.positions?.length >= set.pieces.length;
      // Frame count alone is not enough: several layouts share a count, and the
      // first match is often a scattered arrangement — the wall the shopper
      // reviewed would silently rearrange. Name the counterpart layout per set
      // layout, and only fall back to count-matching when it is missing.
      const named = WALL_LAYOUTS[set.layout];
      const layout =
        (named && (layouts || []).find((l) => l.handle === named && fits(l))) ||
        (layouts || []).find(fits);
      if (!layout) return null;
      const sel = this.sku.state.confirmed;
      const assignments = {};
      set.pieces.forEach((p, i) => {
        // Keyed dynamic-N: shared-wall hydration matches assignments against
        // DYNAMIC layout frame ids (dynamic-1..N), not the static layout's
        // numeric position ids — numeric keys render an empty wall.
        assignments[`dynamic-${i + 1}`] = {
          type: 'catalog',
          productHandle: p.handle,
          productId: p.productId,
          variantId: p.variantId,
          title: p.title || '',
          imageUrl: p.imageUrl || '',
          frameColor: sel.frame_color || 'black',
          mountType: sel.border_option || 'no-border',
          framingOption: sel.framing_option || 'classic-frame',
        };
      });
      // The shared-wall view lays these frames out with the advisor's DYNAMIC
      // engine, which takes arrangement, orientation and size from the wall's
      // layoutSnapshot — not from `layoutHandle`'s static positions. Without
      // one it defaulted to its salon algorithm with mixed-size portrait
      // frames, so a set reviewed as three same-size landscapes in a row
      // opened as three different-size portraits. Send what the shopper saw:
      // every piece at the one configured size, in our own grid's topology.
      const dims = this.sku.getSizeDimensions?.();
      const [cols, rows] = this.#gridFor(set.layout, set.pieces.length);
      const layoutSnapshot = wallLayoutSnapshot({
        layout: set.layout,
        pieceCount: set.pieces.length,
        sizeHandle: dims?.size || sel.size_option,
        widthInches: dims?.widthInches,
        heightInches: dims?.heightInches,
        cols,
        rows,
      });
      const res = await fetch(`${root}a/advisor/api/walls`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          layoutHandle: layout.handle,
          frameAssignments: assignments,
          ...(layoutSnapshot ? { layoutSnapshot } : {}),
          name: set.name || this.i18n('i18nFallbackName', { count: set.pieces.length }),
          visibility: 'shared',
        }),
      });
      if (!res.ok) return null;
      const { shortId } = await res.json();
      if (!shortId) return null;
      return {
        shortId,
        layoutHandle: layout.handle,
        // The cart's room preview maps artworks onto frames by POSITION id
        // (_position per line ↔ layout.positions[].id). Without them every
        // frame renders empty.
        positionIds: (layout.positions || []).map((p) => p.id ?? p.position_id),
      };
    } catch (_) {
      return null;
    }
  }

  // Swap tray: candidates similar to this row's piece, coherence-ranked
  // against the REST of the wall by the fy-similar-pieces API. Selecting a
  // candidate mutates the working set, re-renders the sheet, and REOPENS the
  // tray with the same candidate pool (state passed through `preload`) so the
  // user can visually A/B options — including swapping back to the original.
  async #toggleTray(row, set, index, preload = null) {
    const existing = row.querySelector('[data-bs-tray]');
    if (existing && !preload) {
      existing.remove();
      this.#openTray = null;
      return;
    }
    this.refsQ('[data-bs-sheet]')?.querySelectorAll('[data-bs-tray]').forEach((t) => t.remove());

    // Pool state survives swaps: the original piece stays swappable-back,
    // load-more pagination continues where it left off, and the rail reopens at
    // the scroll offset the shopper left it at.
    const state = preload || {
      pool: [set.pieces[index]],
      offset: 0,
      seen: new Set([set.pieces[index].handle]),
      exhausted: false,
      scrollLeft: 0,
    };

    const tray = document.createElement('div');
    tray.className = 'bundle-save__tray';
    tray.setAttribute('data-bs-tray', '');
    const head = document.createElement('div');
    head.className = 'bundle-save__tray-head';
    const title = document.createElement('span');
    title.textContent = this.i18n('i18nSwapTitle');
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'bundle-save__tray-close';
    close.setAttribute('aria-label', this.i18n('i18nClose') || '');
    close.textContent = '\u00d7';
    close.addEventListener('click', (e) => {
      e.stopPropagation();
      tray.remove();
      this.#openTray = null;
    });
    head.append(title, close);
    const alts = document.createElement('div');
    alts.className = 'bundle-save__tray-alts';
    alts.setAttribute('data-bs-tray-alts', '');
    // Selecting a candidate rebuilds the tray; without this the rail jumps back
    // to the first tile and the shopper loses the run of options they scrolled to.
    alts.addEventListener(
      'scroll',
      () => { state.scrollLeft = alts.scrollLeft; },
      { passive: true }
    );
    if (!preload) {
      // Skeleton tiles shaped like the loaded alts — the tray opens at its
      // final height and candidates replace placeholders instead of popping in.
      // Shaped to the current piece's own aspect, not a fixed portrait
      // default, so a landscape/square print doesn't flash a portrait
      // placeholder before its real crop loads in.
      const skeletonAspect = set.pieces[index].aspect || 0.8;
      for (let i = 0; i < 5; i++) {
        const ph = document.createElement('span');
        ph.className = 'bundle-save__alt-skeleton';
        ph.style.setProperty('--bs-alt-aspect', skeletonAspect);
        alts.appendChild(ph);
      }
    }
    tray.append(head, alts);

    // Advisor escape hatch: save the working wall via the walls API and land
    // the user in /a/advisor/w/<shortId> with it pre-loaded (the share-wall
    // flow; the bare shortId path dead-ends at empty discover). Falls back to
    // a seeded advisor entry.
    const advisor = document.createElement('button');
    advisor.type = 'button';
    advisor.className = 'bundle-save__tray-advisor';
    advisor.textContent = this.i18n('i18nSwapAdvisor');
    advisor.addEventListener('click', async (e) => {
      e.stopPropagation();
      advisor.disabled = true;
      if (window.posthog?.capture) {
        window.posthog.capture('bundle_save_advisor_handoff', {
          anchor: this.dataset.anchorHandle, set_id: set.setId,
        });
      }
      const root = window.Shopify?.routes?.root || '/';
      const saved = await this.#saveWall(set);
      window.location.href = saved
        ? `${root}a/advisor/w/${saved.shortId}`
        : `${root}a/advisor?seed=${encodeURIComponent(this.dataset.anchorHandle)}`;
    });
    tray.appendChild(advisor);

    row.appendChild(tray);
    this.#openTray = { index, state };
    if (!preload && window.posthog?.capture) {
      window.posthog.capture('bundle_save_swap_opened', {
        anchor: this.dataset.anchorHandle, set_id: set.setId, artwork: set.pieces[index].handle,
      });
    }

    const LIMIT = 6;
    const root = window.Shopify?.routes?.root || '/';

    const fetchBatch = async () => {
      const params = new URLSearchParams({
        handle: set.pieces[index].handle,
        exclude: [this.dataset.anchorHandle, ...set.pieces.map((p) => p.handle), ...state.seen].join(','),
        context: set.pieces.filter((_, i) => i !== index).map((p) => p.handle).join(','),
        limit: String(LIMIT),
        offset: String(state.offset),
      });
      const currency = window.Shopify?.currency?.active;
      if (currency) params.set('currency', currency);
      const res = await fetch(`${root}apps/chat-agent/api/fy-similar-pieces?${params}`);
      if (!res.ok) throw new Error(`similar-pieces ${res.status}`);
      const { pieces } = await res.json();
      state.offset += LIMIT * 4; // server offset is over the raw retrieval window
      const fresh = (Array.isArray(pieces) ? pieces : []).filter((p) => !state.seen.has(p.handle));
      for (const piece of fresh) {
        state.seen.add(piece.handle);
        state.pool.push(piece);
      }
      if (fresh.length < LIMIT) state.exhausted = true;
      return fresh;
    };

    const renderPool = () => {
      alts.textContent = '';
      const currentHandle = set.pieces[index].handle;
      for (const piece of state.pool) {
        const isCurrent = piece.handle === currentHandle;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'bundle-save__alt';
        if (isCurrent) btn.classList.add('is-current');
        btn.disabled = isCurrent;
        btn.setAttribute('aria-label', piece.title || '');
        // Same fix as the piece-row thumb: box follows the candidate's own
        // aspect instead of a fixed 4/5 portrait crop.
        btn.style.setProperty('--bs-alt-aspect', piece.aspect || 0.8);
        const img = document.createElement('img');
        img.loading = 'lazy';
        img.alt = '';
        if (piece.imageUrl) img.src = piece.imageUrl;
        btn.appendChild(img);
        if (!isCurrent) {
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const out = set.pieces[index].handle;
            set.pieces[index] = piece;
            if (window.posthog?.capture) {
              window.posthog.capture('bundle_save_piece_swapped', {
                anchor: this.dataset.anchorHandle, set_id: set.setId,
                out, in: piece.handle,
              });
            }
            // Re-render the sheet, then REOPEN this tray with the same pool so
            // the user keeps trying options visually.
            this.#renderSheet(set);
            const rows = this.refsQ('[data-bs-sheet-body]')?.querySelectorAll('.bundle-save__sheet-piece');
            const freshRow = rows?.[index];
            if (freshRow) this.#toggleTray(freshRow, set, index, state);
          });
        }
        alts.appendChild(btn);
      }
      if (!state.exhausted) {
        const more = document.createElement('button');
        more.type = 'button';
        more.className = 'bundle-save__alt bundle-save__alt--more';
        more.setAttribute('aria-label', this.i18n('i18nSwapMore'));
        more.textContent = '+';
        // A flex row's height is set by its tallest item regardless of
        // align-items — leaving this tile at the fixed 4/5 CSS fallback (a
        // portrait ratio) forced the whole rail tall even when every real
        // candidate was landscape or square, showing as a big gap between
        // the images and the advisor link below. Match the current piece's
        // own aspect so the rail's height reflects what's actually in it.
        more.style.setProperty('--bs-alt-aspect', set.pieces[index].aspect || 0.8);
        more.addEventListener('click', async (e) => {
          e.stopPropagation();
          // Fetching a fresh batch takes a beat; without a visible busy state
          // the + reads as broken and gets clicked again.
          more.disabled = true;
          more.classList.add('is-loading');
          more.textContent = '';
          try {
            await fetchBatch();
            if (tray.isConnected) renderPool();
          } catch (_) {
            state.exhausted = true;
            if (tray.isConnected) renderPool();
          }
        });
        alts.appendChild(more);
      }
      // Hold the rail where the shopper left it (the tiles are laid out
      // synchronously above, so the scroll offset is assignable right away).
      if (state.scrollLeft) alts.scrollLeft = state.scrollLeft;
    };

    if (preload) {
      renderPool();
      return;
    }
    try {
      const fresh = await fetchBatch();
      if (!tray.isConnected) return; // closed while loading
      if (!fresh.length) {
        alts.textContent = this.i18n('i18nSwapEmpty');
        return;
      }
      renderPool();
    } catch (_) {
      if (tray.isConnected) alts.textContent = this.i18n('i18nSwapEmpty');
    }
  }

  renderCards() {
    const carousel = this.refsQ('[data-bs-carousel]');
    carousel.textContent = ''; // also clears the loading skeleton
    this.removeAttribute('aria-busy');
    this.cardEls = new Map();
    this.frameEls = new Map();
    const sel = this.sku.state.confirmed;
    const mockCfg = this.sku.getResolvedMockConfig() || {};
    const paper = this.#paperDims();

    for (const set of this.sets) {
      const li = document.createElement('li');
      li.className = 'bundle-save__card';
      li.dataset.setId = set.setId;

      const frames = document.createElement('div');
      frames.className = 'bundle-save__frames';
      frames.setAttribute('data-bs-frames', '');
      this.#applyWallGrid(frames, set.layout, set.pieces.length);
      const els = set.pieces.map((piece, i) =>
        this.#buildFrameEl(piece, mockCfg, paper, `bs-${set.setId}-${i}`)
      );
      els.forEach((el) => {
        const cell = document.createElement('div');
        cell.className = 'bundle-save__cell';
        cell.appendChild(el);
        frames.appendChild(cell);
      });
      this.frameEls.set(set.setId, els);

      const badge = document.createElement('span');
      badge.className = 'bundle-save__badge';
      badge.setAttribute('data-bs-badge', '');

      const name = document.createElement('p');
      name.className = 'bundle-save__name';
      name.textContent = set.name || this.i18n('i18nFallbackName', { count: set.pieces.length });

      const meta = document.createElement('p');
      meta.className = 'bundle-save__meta';
      meta.setAttribute('data-bs-meta', '');
      meta.textContent = this.#metaText(set);

      const priceRow = document.createElement('p');
      priceRow.className = 'bundle-save__price';
      priceRow.innerHTML =
        '<s data-bs-price-strike></s> <strong data-bs-price-bundle></strong>';

      // Whole card is one tap target: the sheet carries the add CTA.
      li.addEventListener('click', () => this.#openSheet(set));

      li.append(frames, badge, name, meta, priceRow);
      carousel.appendChild(li);
      this.cardEls.set(set.setId, li);
      this.repriceCard(set, sel);
    }
  }

  repriceCard(set, sel) {
    const li = this.cardEls.get(set.setId);
    if (!li) return;
    const { addable, total, saving, bundle } = setTotals(set, sel);
    const strike = li.querySelector('[data-bs-price-strike]');
    const bundleEl = li.querySelector('[data-bs-price-bundle]');
    const badge = li.querySelector('[data-bs-badge]');
    const meta = li.querySelector('[data-bs-meta]');
    if (meta) meta.textContent = this.#metaText(set); // dims/framing track the configurator
    if (!addable) {
      strike.textContent = '';
      bundleEl.textContent = this.i18n('i18nUnavailable');
      badge.hidden = true;
      return;
    }
    strike.textContent = this.sku._formatPrice(total);
    bundleEl.textContent = this.sku._formatPrice(bundle);
    badge.hidden = false;
    badge.textContent = this.i18n('i18nSaveBadge', {
      amount: this.sku._formatPrice(saving),
    });
  }

  #addedSets = new Set();

  // Idempotency is COMPOSITION-aware, not just set-aware: a swap changes the
  // pieces, and a customer who added the curated set must be able to add a
  // customised variant of it. Same composition twice stays blocked.
  #setSignature(set) {
    // The signature is what "already added" means, so it has to cover
    // everything that makes a DIFFERENT cart line: the composition (swaps) and
    // the configuration. Keying on pieces alone blocked re-adding the same set
    // in another frame/mount/size — a legitimate second purchase that resolves
    // to different variants and different line properties.
    const sel = this.sku.state.confirmed;
    const config = [sel.size_option, sel.framing_option, sel.frame_color, sel.border_option].join('|');
    return `${set.setId}|${set.pieces.map((p) => p.handle).join(',')}|${config}`;
  }

  async #addSet(set, button) {
    // Idempotent per set: the sheet rebuilds its CTA on every open, so the
    // guard lives on the unit, not the button.
    const signature = this.#setSignature(set);
    if (this.#addedSets.has(signature) || button.classList.contains('is-added')) return;
    const originalLabel = button.textContent;
    button.disabled = true;
    button.textContent = this.i18n('i18nAdding');
    const sel = this.sku.state.confirmed;
    try {
      const root = window.Shopify?.routes?.root || '/';
      // Variant ids arrive with the suggestion, so there is no resolve step.
      //
      // This used to POST every position to /a/gallery-wall/api/cart-resolve to
      // turn (product, size, framing) into a variant id. That round trip was
      // never needed: the suggestions API already queries each product's
      // variants to read its configurator, so it can return the id it already
      // has. Removing it takes out the app-proxy call that had to be retried
      // for flakiness, and the "Couldn't add the set" failure path with it.
      const missing = set.pieces.filter((p) => !p.variantId);
      if (missing.length) throw new Error('set has unpurchasable pieces');
      if (!this.#anchorIsArtPrint()) throw new Error('set is not addable on a canvas selection');
      const wallId = crypto.randomUUID();
      // Persist the wall so the cart's bundle card can reopen it in the builder
      // with the actual artworks in place. Best-effort: a failure here costs
      // the Edit deep-link, not the add.
      const savedWall = await this.#saveWall(set);
      // Everything below the variant id is a property of the SELECTION, not
      // of the artwork — every piece in a set shares one size, framing, border
      // and frame colour — so the PDP's own hidden inputs are exactly right for
      // all of them. Reading them keeps the set's lines identical to what the
      // PDP itself would add, without a second source of truth.
      const shared = this.#pdpProperties();
      // What the sheet promised, in cents. Without it the order records which
      // discount RULE applied (_bundle_discount_key) and how much was taken
      // off, but never the amount the shopper was shown — so "were they
      // charged what they were offered" could only be answered by matching a
      // PostHog add event on timing and anchor, which failed for two of the
      // first three bundle orders. With it every order reconciles on its own.
      //
      // SET-level, repeated on every line exactly as _bundle_discount_key is.
      // Reconciliation groups lines by _gallery_wall_id, sums the Gallery Wall
      // Discount allocations across them, and compares that one total against
      // this one value. Do not sum this field.
      const promisedSaving = setTotals(set, sel).saving;
      const items = set.pieces.map((piece, i) => {
        const properties = {
          ...shared,
          _gallery_wall_id: wallId,
          // Position + artwork URL are what the cart's room preview maps
          // frames from; without them it renders the layout with empty frames.
          _position: savedWall?.positionIds?.[i] ?? i + 1,
          // Canonical layout handle, not our internal key: the cart's bundle
          // card builds /a/gallery-wall/build/<this> and an unknown handle
          // dead-ends on the gallery-wall landing page.
          _gallery_wall_layout: savedWall?.layoutHandle || WALL_LAYOUTS[set.layout] || set.layout,
          // The config key this sheet priced with. The layout above is a
          // canonical Gallery Wall handle, which is a DIFFERENT entry in the
          // discount config with a different amount — without this, checkout
          // charges a saving the shopper was never shown.
          _bundle_discount_key: set.discount_key || set.layout,
        };
        // Guarded rather than written blind: an unpriceable selection makes
        // setTotals return no saving, and a line property of `undefined`
        // serialises into the cart as the string "undefined", which would read
        // as a promise that was made.
        if (Number.isFinite(promisedSaving)) properties._bundle_promised_saving = promisedSaving;
        if (savedWall) properties._gallery_wall_short_id = savedWall.shortId;
        if (piece?.imageUrl) properties._artwork_image_url = piece.imageUrl;
        if (piece?.title) properties._artwork_title = piece.title;
        // Always name the wall: without this the cart card falls back to a
        // bare "Gallery Wall" instead of the set the shopper actually added.
        properties._gallery_wall_name =
          set.name || this.i18n('i18nFallbackName', { count: set.pieces.length });
        // Defensive: the API returns a bare numeric id, but cart/add.js
        // silently rejects the whole batch if a gid ever slips through.
        const id = String(piece.variantId).replace('gid://shopify/ProductVariant/', '');
        return { id, quantity: 1, properties };
      });
      // Where the dataLayer ends BEFORE the add, so #reportPieceAdds can tell
      // which add_to_cart pushes the Stape embed made for this add from any
      // that were already on the queue.
      const dataLayerMark = Array.isArray(window.dataLayer) ? window.dataLayer.length : 0;
      const addRes = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!addRes.ok) throw new Error('add failed');
      button.classList.add('is-added');
      this.#addedSets.add(signature);
      button.textContent = this.i18n('i18nSetAdded');
      let itemCount;
      try {
        itemCount = (await (await fetch(`${root}cart.js`)).json()).item_count;
      } catch (_) { /* non-fatal */ }
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: { data: { itemCount: typeof itemCount === 'number' ? itemCount : 0 } },
        })
      );
      if (window.posthog?.capture) {
        window.posthog.capture('bundle_save_set_added', {
          anchor: this.dataset.anchorHandle,
          set_id: set.setId,
          curation_id: set.curationId,
          layout: set.layout,
          piece_count: set.pieces.length,
          savings_cents: setTotals(set, sel).saving,
        });
      }
      // A set add is an add: it counts toward the browser's first-ever add the
      // same as any other. Same handle as the cart:update path, so the claim is
      // made once and only one of the two can win.
      window.fyUniqueAtc?.report('bundle-save');
      // Not awaited: it waits on the embed's push, and the shopper should not
      // wait on analytics. Failures here must never surface as an add error.
      this.#reportPieceAdds(set, sel, dataLayerMark).catch((e) => {
        console.warn('[bundle-save] piece-level add reporting failed:', e?.message || e);
      });
    } catch (err) {
      // Was `catch (_)`. The shopper-facing message is deliberately vague, but
      // swallowing the reason entirely meant a permanent failure and a
      // transient one looked identical from the console — a swap making every
      // set unaddable (fy-similar-pieces omitted variantId) took a browser
      // session to find because nothing was logged.
      console.warn('[bundle-save] add failed:', err?.message || err);
      button.classList.remove('is-added');
      button.textContent = this.i18n('i18nAddError');
      setTimeout(() => {
        button.textContent = originalLabel;
      }, 2000);
    } finally {
      button.disabled = false;
    }
  }

  /**
   * Report one add-to-cart per PIECE, for PostHog and for the Meta/GTM path.
   *
   * A set is added as ONE `cart/add.js` call carrying N line items, and the two
   * destinations need different things from that, so they get different things:
   *
   *   • PostHog gets ONE event. Its `product_added_to_cart` is fired by a
   *     cart:update listener in posthog-events.js that whitelists `data.source`
   *     (`product-form-component` / `bundle-submission` / `readymade-frame-pdp`).
   *     The dispatch above carries no source, so the listener bails and a set
   *     add produced NO event at all — 13 of 29 set-adders over the
   *     bundle-save-ab run never registered one. The funnel step asks whether a
   *     visitor added anything, so one event answers it; N would inflate
   *     product-level add counts and every cost-per-add-to-cart derived from
   *     them. Per-piece detail already lives on `bundle_save_set_added`.
   *   • Meta gets N. The Stape app embed owns the ecommerce dataLayer and with
   *     it Meta's AddToCart (see snippets/stape-gtm-deferred.liquid), and it
   *     fires once per `cart/add.js` CALL, not once per line. Captured live
   *     10 Sep 2026 by adding two line items in one call: exactly ONE
   *     `add_to_cart_stape` push, carrying ONE item, with `value` set to that
   *     one item's price. A six-piece salon set reached Meta as a single print.
   *     Meta needs an add per PRODUCT so the signal ties to catalogue items, so
   *     the missing pieces are pushed here.
   *
   * The batch add is deliberately left intact. Splitting it into N calls would
   * make the platform emit N correct events on its own, but it turns an atomic
   * add into a partial-failure surface, and a shopper left holding three of
   * five prints is a worse defect than an under-counted conversion. This is a
   * measurement problem, so it gets a measurement fix.
   *
   * The embed's own event is neither suppressed nor duplicated: whichever piece
   * it already reported is skipped, so the total is exactly N. If it never
   * fires (disabled, consent declined, GTM blocked) every piece is pushed,
   * which is the right answer in that case too.
   *
   * @param {object} set - the set that was added
   * @param {object} sel - the selection it was priced at
   * @param {number} mark - dataLayer length captured before the add
   */
  async #reportPieceAdds(set, sel, mark) {
    const pieces = Array.isArray(set?.pieces) ? set.pieces : [];
    if (!pieces.length) return;
    const currency =
      window.Shopify?.currency?.active || this.dataset.currency || undefined;
    const priceOf = (piece) => {
      const cents = pieceTotalCents(piece.pricing, sel);
      return typeof cents === 'number' ? cents : null;
    };

    // PostHog gets ONE event, not one per piece. The shopper performed a single
    // add, and the funnel's add-to-cart step asks "did this visitor put
    // something in their cart" — N events would answer that question wrongly and
    // would also inflate product-level add counts and every cost-per-add-to-cart
    // figure derived from them. The per-piece breakdown already exists on
    // `bundle_save_set_added`; this event only has to make the adder appear in
    // the funnel at all, which today they do not.
    if (window.posthog?.capture) {
      const total = pieces.reduce((sum, piece) => {
        const cents = priceOf(piece);
        return sum === null || cents === null ? null : sum + cents;
      }, 0);
      window.posthog.capture('product_added_to_cart', {
        // `source` names the surface, using the same vocabulary as the
        // cart:update listener's whitelist, so bundle adds stay separable.
        source: 'bundle-save',
        product_id: this.dataset.anchorProductId ?? null,
        variant_id: null,
        // The set is one add of N lines: quantity carries the line count so a
        // units-per-add read stays honest without a second event.
        quantity: pieces.length,
        cart_value_cents: total,
        currency: currency || null,
        value: typeof total === 'number' ? total / 100 : null,
        set_id: set.setId,
        layout: set.layout,
        piece_count: pieces.length,
        anchor: this.dataset.anchorHandle,
      });
    }

    const dl = window.dataLayer;
    if (!Array.isArray(dl)) return;

    // Two adds inside the same window would otherwise read overlapping slices
    // and the second could claim the first's embed push. Give each call a slice
    // that starts where the last one stopped looking.
    const from = Math.max(mark, this.#atcDiffCursor);

    // WAIT FOR the embed's push rather than sleeping a guessed interval. A
    // fixed delay is wrong in both directions: too short and we push a
    // supplementary event for a piece the embed is about to report, which
    // double-counts it to Meta — the exact defect this code exists to fix, in
    // reverse — and too long delays every add for the slowest case. The embed
    // runs through Shopify's web-pixel sandbox, so its latency is neither
    // synchronous nor bounded by anything we control, and the connections this
    // unit is designed around (1.6Mbps / 4x CPU, see snippets/bundle-save.liquid)
    // are where a guess fails.
    //
    // Nothing here is awaited by the shopper, so waiting longer costs nothing
    // but reporting latency. The cap only decides how long we hold out before
    // concluding the embed will never fire — blocked, consent declined, GTM
    // absent — in which case every piece is ours to push.
    const deadline = performance.now() + EMBED_PUSH_WAIT_MS;
    while (performance.now() < deadline) {
      if (reportedVariantIds(dl.slice(from)).size > 0) break;
      await new Promise((resolve) => setTimeout(resolve, EMBED_PUSH_POLL_MS));
    }

    const pending = piecesToPush(pieces, dl.slice(from));
    // Claim the slice before pushing, so our own entries are never mistaken for
    // the embed's by a later add.
    this.#atcDiffCursor = dl.length;
    for (const piece of pending) {
      dl.push(pieceAtcPush(piece, priceOf(piece), currency));
    }
  }
}

customElements.define('bundle-save-unit', BundleSaveUnit);
